import React from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContrastOutlinedIcon from '@mui/icons-material/ContrastOutlined';
import DensitySmallOutlinedIcon from '@mui/icons-material/DensitySmallOutlined';
import MotionPhotosOffOutlinedIcon from '@mui/icons-material/MotionPhotosOffOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';

import { colors } from '../../theme/tokens';
import { useInterfacePreferences, type InterfacePreferences } from '../GridlineProvider';
import { ThemeModeSelect } from './ThemeModeSelect';

const rowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 1fr) auto' },
  gap: { xs: 1, sm: 2 },
  alignItems: 'center',
  py: 1.25,
};

function SettingLabel({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
      <Box sx={{ display: 'flex', color: 'text.secondary', pt: 0.15, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, lineHeight: 1.3 }}>{title}</Typography>
        <Typography sx={{ mt: 0.25, fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>{description}</Typography>
      </Box>
    </Stack>
  );
}

function ChoiceControl<Key extends keyof InterfacePreferences>({
  preferenceKey,
  options,
}: {
  preferenceKey: Key;
  options: Array<{ value: InterfacePreferences[Key]; label: string }>;
}) {
  const { preferences, setPreference } = useInterfacePreferences();

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={preferences[preferenceKey]}
      onChange={(_event, value: InterfacePreferences[Key] | null) => {
        if (value !== null) setPreference(preferenceKey, value);
      }}
      sx={{
        justifySelf: { sm: 'end' },
        '& .MuiToggleButton-root': {
          minHeight: 30,
          px: 1,
          py: 0.45,
          borderColor: 'divider',
          color: 'text.secondary',
          fontSize: '0.68rem',
          fontWeight: 700,
          textTransform: 'none',
          '&.Mui-selected': {
            bgcolor: 'rgba(124,58,237,0.12)',
            color: colors.primary[200],
          },
        },
      }}
    >
      {options.map((option) => <ToggleButton key={String(option.value)} value={option.value}>{option.label}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

export function InterfaceSettingsPanel() {
  const { preferences, setPreference, resetPreferences } = useInterfacePreferences();

  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.2, p: 1.35 }}>
      <Typography sx={{ fontSize: '0.88rem', fontWeight: 900 }}>Interface</Typography>
      <Typography sx={{ mt: 0.35, mb: 0.8, fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.45 }}>
        Make Paddiq easier to read and comfortable to use on this device. Changes apply immediately.
      </Typography>

      <Box sx={rowSx}>
        <SettingLabel icon={<SettingsBrightnessOutlinedIcon />} title="Color theme" description="Use light, dark, or your device setting." />
        <ThemeModeSelect sx={{ justifySelf: { sm: 'end' } }} />
      </Box>
      <Divider />
      <Box sx={rowSx}>
        <SettingLabel icon={<TextFieldsRoundedIcon />} title="Text size" description="Increase labels, tables, and controls throughout the app." />
        <ChoiceControl preferenceKey="textSize" options={[
          { value: 'default', label: 'Default' },
          { value: 'large', label: 'Large' },
          { value: 'extra-large', label: 'Extra large' },
        ]} />
      </Box>
      <Divider />
      <Box sx={rowSx}>
        <SettingLabel icon={<DensitySmallOutlinedIcon />} title="Control spacing" description="Choose a tight layout or larger, easier targets." />
        <ChoiceControl preferenceKey="density" options={[
          { value: 'compact', label: 'Compact' },
          { value: 'comfortable', label: 'Comfortable' },
        ]} />
      </Box>
      <Divider />
      <Box sx={rowSx}>
        <SettingLabel icon={<ViewSidebarOutlinedIcon />} title="Sidebar on startup" description="Remember its last state or always start open or collapsed." />
        <ChoiceControl preferenceKey="sidebarDefault" options={[
          { value: 'remember', label: 'Remember' },
          { value: 'open', label: 'Open' },
          { value: 'collapsed', label: 'Collapsed' },
        ]} />
      </Box>
      <Divider />
      <Box sx={rowSx}>
        <SettingLabel icon={<MotionPhotosOffOutlinedIcon />} title="Reduce motion" description="Minimize animation and interface movement." />
        <Switch checked={preferences.reduceMotion} onChange={(event) => setPreference('reduceMotion', event.target.checked)} inputProps={{ 'aria-label': 'Reduce motion' }} sx={{ justifySelf: { sm: 'end' } }} />
      </Box>
      <Divider />
      <Box sx={rowSx}>
        <SettingLabel icon={<ContrastOutlinedIcon />} title="Higher contrast" description="Strengthen borders and secondary text." />
        <Switch checked={preferences.highContrast} onChange={(event) => setPreference('highContrast', event.target.checked)} inputProps={{ 'aria-label': 'Higher contrast' }} sx={{ justifySelf: { sm: 'end' } }} />
      </Box>

      <Button
        variant="text"
        size="small"
        startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 15 }} />}
        onClick={resetPreferences}
        sx={{ mt: 0.6, px: 0.5, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'none' }}
      >
        Reset interface settings
      </Button>
    </Box>
  );
}
